<template>
  <div v-if="isLoading" class="d-flex justify-center align-center">
    <v-row no-gutters class="px-12 py-6">
      <v-col cols="12" class="d-flex justify-center align-center">
        <v-progress-circular
          indeterminate
          color="#2661FF"
          size="80"
        ></v-progress-circular>
      </v-col>
    </v-row>
  </div>
  <div v-else>
    <v-card class="rounded-lg" flat>
      <v-card-title class="px-10 pt-8">
        <p>{{ $t('organization.details.tabs.addons.title') }}</p>
      </v-card-title>
      <v-card-text>
        <v-list two-line>
          <template v-for="item of computedAddons">
            <v-divider />
            <v-list-item>
              <v-list-item-content class="ml-2">
                <v-list-item-title>
                  <span>{{ item.name }}</span>
                </v-list-item-title>
                <v-list-item-subtitle
                  v-if="selectedAddons.includes(item.id) && item.error"
                  class="error--text mt-1"
                >
                  <span class="caption">{{ item.errorMessage }}</span>
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else class="mt-1">
                  <span class="caption">{{ item.description }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="mx-4">
                <v-row :class="item.config ? 'mt-1' : ''">
                  <v-col cols="6" :class="!item.config ? 'mr-15' : ''">
                    <v-switch
                      v-model="selectedAddons"
                      :value="item.id"
                      :disabled="item.disabled"
                      :readonly="isUpdateLoading"
                      color="#2661FF"
                    ></v-switch>
                  </v-col>
                  <v-col cols="6" v-if="item.config">
                    <v-btn
                      icon
                      color="grey"
                      @click="dispatchMethod(item.configDispatch)"
                      class="pb-2 text--darken-3"
                    >
                      <v-icon size="28">
                        edit_square
                      </v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-list-item-action>
            </v-list-item>
          </template>
          <template>
            <v-divider />
            <v-list-item>
              <v-list-item-content class="ml-2">
                <v-list-item-title>
                  <span>Donation Logs</span>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="mx-4">
                <v-row>
                  <v-col cols="6" class="mr-15">
                    <v-switch
                      v-model="donationLogs"
                      color="#2661FF"
                      @click="updateDonationLogs"
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
        <v-card-actions class="justify-end px-6">
          <v-btn
            color="#2661FF"
            :loading="isUpdateLoading"
            :disabled="invalidAddons"
            @click="updateAddons"
          >
            <span class="white--text">
              {{ $t('organization.details.tabs.modules.save') }}
            </span>
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'
export default {
  name: 'organizationComplements',
  data() {
    return {
      isLoading: false,
      isUpdateLoading: false,
      selectedAddons: [],
      donationLogs: false
    }
  },
  async created() {
    try {
      this.isLoading = true
      await this.getOrganizationAvaliableAddons(this.organization.id)
      await this.getAccounts({ organizationId: this.organization.id })

      this.donationLogs = !!JSON.parse(JSON.stringify(this.organization.donation_logs))
    } catch (error) {
      this.$noty.error('Error at complements')
    } finally {
      this.isLoading = false
    }
  },
  watch: {
    orgAddons(newVal, oldVal) {
      const addons = JSON.parse(JSON.stringify(this.addons))
      const selected = addons.forEach(integration => {
        newVal.forEach(orgIntegration => {
          if (integration.id === orgIntegration.id) {
            this.selectedAddons.push(integration.id)
          }
        })
      })
    }
  },
  computed: {
    ...mapState({
      organization: ({ organization: { organization } }) => organization,
      addons: ({ organization: { addons } }) => addons,
      orgAddons: ({ organization: { orgAddons } }) => orgAddons
    }),
    ...mapGetters({
      accounts: 'accounts/accounts'
    }),
    computedAddons() {
      return this.addons.map(e => {
        if (e.name.toLowerCase() === 'certifications') {
          return {
            ...e,
            config: true,
            configDispatch: {
              name: 'openAmazonSESConfig',
              value: 'certificate'
            },
            error:
              !this.certificateAccount ||
              (this.certificateAccount &&
                this.certificateAccount.config.invalidKeys),
            errorMessage: this.$t(
              'organization.details.tabs.integrations.amazon_ses.invalid_keys'
            )
          }
        } else if (e.name.toLowerCase() === 'paymentlimiter') {
          return {
            ...e,
            config: true,
            configDispatch: {
              name: 'togglePaymentLimiterConfig',
              value: true
            }
          }
        } else if (e.name.toLowerCase() === 'antifraud') {
          return {
            ...e,
            config: true,
            configDispatch: {
              name: 'toggleAntiFraudConfig',
              value: true
            }
          }
        } else {
          return e
        }
      })
    },
    invalidAddons() {
      const addon = this.computedAddons.find(e => e.error)
      if (addon) {
        return this.selectedAddons.includes(addon.id)
      }
      return false
    },
    certificateAccount() {
      return this.accounts.find(({ type }) => type === 'certificate')
    }
  },
  methods: {
    ...mapActions({
      getOrganizationAvaliableAddons:
        'organization/getOrganizationAvaliableAddons',
      updateOrganizationAvaliableAddons:
        'organization/updateOrganizationAvaliableAddons',
      getAccounts: 'accounts/getAccounts'
    }),
    ...mapMutations({
      setType: 'accounts/setType',
      toggleAmazonSESConfig: 'accounts/toggleAmazonSESConfig',
      togglePaymentLimiterConfig: 'organization/togglePaymentLimiterConfig',
      toggleAntiFraudConfig: 'organization/toggleAntiFraudConfig'
    }),
    dispatchMethod(dispatch) {
      const { name, value } = dispatch
      this[name](value)
    },
    openAmazonSESConfig(type) {
      this.setType(type)
      this.toggleAmazonSESConfig(true)
    },
    async updateAddons() {
      this.isUpdateLoading = true
      const addons = this.selectedAddons
      try {
        const response = (
          await this.updateOrganizationAvaliableAddons({
            id: this.organization.id,
            body: {
              ids: addons
            }
          })
        ).data
        this.isUpdateLoading = false
        if (response.success) {
          this.$noty.success(
            this.$t('organization.details.tabs.addons.success')
          )
        } else {
          this.$noty.error(this.$t('organization.details.tabs.addons.error'))
        }
      } catch (error) {
        this.selectedAddons = addons
        this.$noty.error(this.$t('organization.details.tabs.addons.error'))
      } finally {
        this.isUpdateLoading = false
      }
    },
    async updateDonationLogs() {
      try {
        const res = await axios.post(
          `admin/donation-log-preferences`,
          { donationLogs: this.donationLogs, organizationId: this.organization.id }
        )

        console.log({ res })
        this.$noty.success('updateDonationLogs success')

      } catch (error) {
        console.log({error})
        this.$noty.error('Error at updateDonationLogs')
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
